/**
 * Client Portal Service history (plan section 19): one customer's past approved work, judged by each
 * task's report rule. Read-only and portal-only; the tenant has no board, so the base manifest only
 * names the entity and the portal block carries everything, as Services does.
 */
import { ActivityEntityName, DomainType, EntityViewDisplay, ServiceHistoryRow } from '@jms/model';
import { clientPaths } from '@jms/tenant/module.jms.authorisation';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { FilterPeriodPreset } from '@workbuddy/ui/v2/blocks/filter';
import { EntityBoardPeriodLimit } from '@workbuddy/ui/v2/blocks/entity.board/period';
import { clientCsvDownloadCommands } from '../shared/client.csv.download';
import { serviceHistoryCellRenderer } from './presentation/service.history.result.cell.renderer';
import { serviceHistoryColumns } from './presentation/service.history.columns';

// The same CSV as Services, from the same board request, so the file is the rows on screen. Gated on
// the Services path: History is a view of that tab, not a tab of its own.
const serviceHistoryCsvCommands = () => clientCsvDownloadCommands({ entity: ActivityEntityName.ServiceHistory, path: clientPaths.Services, commandId: 'service-history.download-csv', title: 'Task history' });

export const serviceHistoryManifest = registerManifest<ServiceHistoryRow>({
  entity: ActivityEntityName.ServiceHistory,
  slug: 'service-history',
  domains: {
    [DomainType.ClientPortal]: {
      routes: {
        root: '/service-history',
        record: () => null,
      },
      board: {
        supportedDisplays: [EntityViewDisplay.Table],
        defaults: {
          /**
           * History includes today but never future work, so only past and current presets apply.
           */
          period: {
            field: 'date',
            presets: [FilterPeriodPreset.LastWeek, FilterPeriodPreset.LastFortnight, FilterPeriodPreset.LastMonth, FilterPeriodPreset.ThisWeek, FilterPeriodPreset.ThisFortnight, FilterPeriodPreset.ThisMonth],
            // This month, not last: a new plan's last month is often before it started, so the board opened empty.
            defaultPreset: () => FilterPeriodPreset.ThisMonth,
            custom: true,
            // Match the server's inclusive-today boundary; future work belongs on Visits.
            notAfter: EntityBoardPeriodLimit.Today,
          },
          readonly: true,
          // The Result pills are the tenant tabs' alone: the metadata is shared, and the portal strategy counts nothing.
          segmentTiles: false,
          empty: { title: 'No tasks were due in this period.', description: 'Task history lists tasks due up to and including today, with how each was resolved. Future work is on Visits.' },
          table: {
            fillerColumn: true,
            columns: serviceHistoryColumns,
            overrideCellRenderer: serviceHistoryCellRenderer,
          },
          header: {
            title: 'Task history',
            showSearch: true,
            showPaginator: true,
            showViewOptions: false,
            hideAdd: true,
            primaryCommands: serviceHistoryCsvCommands,
            // Readonly drops header commands as mutation entry points; Download CSV writes nothing.
            keepPrimaryCommandsWhenReadonly: true,
          },
        },
      },
    },
  },
});
